import React, { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { func, bool, string, arrayOf } from 'prop-types'
import arrowLeft from '@ingka/ssr-icon/paths/arrow-left'
import cross from '@ingka/ssr-icon/paths/cross'
import shoppingBag from '@ingka/ssr-icon/paths/shopping-bag'
import shoppingBagActive from '@ingka/ssr-icon/paths/shopping-bag-active'
import { useTranslation } from 'react-i18next'
import Icon from 'components/uiElements/Icon'
import { compositionType, productsDataType } from 'models/propTypes'
import FavoriteButtonContainer from 'containers/uiElements/FavoriteButtonContainer'
import { TRACK_LOCATION_DESIGN_SUMMARY } from 'models/tracking/constants'
import Button from '@ingka/button'
import ProductItemContainer from 'containers/designSidepanel/ProductItemContainer'
import { getRenderer } from 'models/refStore/selectors'
import * as Sentry from '@sentry/react'

require('./DesignSummaryModal.scss')

const DesignSummaryModal = ({
    closeModal,
    addRoomToBag,
    hasAddedToBag,
    composition,
    totalPrice,
    localItemNumbers = null,
    openExitPopup,
    addedToRoomItems,
    openBag,
    isModified,
    openDesignLab,
    ikeaNav,
    isMagicWindow,
    onCloseMagicWindow,
    isLoggedIn,
    isSaving,
    onSave,
}) => {
    const panoImageRef = useRef(null)
    const history = useHistory()

    useEffect(() => {
        getRenderer().takeRoomScreenshot(
            (thumb) => {
                const imageElement = panoImageRef && panoImageRef.current

                if (imageElement) {
                    imageElement.src = thumb.thumbDataUrl
                }
            },
            (error) => {
                Sentry.captureException(new Error('Error generating thumbnail from preview', { cause: error }))
            },
        )
    }, [])

    const { t } = useTranslation()
    const compositionName = composition && (composition.name || t('design.default composition name'))

    const onDoneClick = () => {
        // If the user is in a magic window, close the window
        if (isMagicWindow && onCloseMagicWindow) {
            onCloseMagicWindow()
            // else user is in design lab or sugarcube
            // if the design is not saved and user is logged in,
            // open the the save popup
        } else if (isModified && isLoggedIn) {
            openExitPopup()
            // if the user has a saved design return to design your home
        } else if (ikeaNav) {
            closeModal()
            openDesignLab()
            // else return to sugarcube
        } else {
            closeModal()
            // go back to sugarcube dashboard
            history.push(`/dashboard`)
        }
    }

    const onSaveClick = () => {
        if (isModified) {
            // if the user is logged out, login prompt will appear to log in or sign up
            const trackingInfo = {
                SavedFrom: TRACK_LOCATION_DESIGN_SUMMARY,
            }
            onSave(trackingInfo)
        }
    }

    const hasProductsInRoom = addedToRoomItems && addedToRoomItems.length
    const hasItemsToFavorite = localItemNumbers && localItemNumbers.length
    const showSaveButton = !isMagicWindow && isModified
    const saveButtonText = isSaving ? t('designSummary.saving') : t('designSummary.save')

    return (
        <div className="DesignSummaryModal">
            <div className="modalHeader">
                <Icon svg code="logo_ikea" className="logoHome" />
                <div className="ctasGroup">
                    <Button
                        onClick={openBag}
                        type="tertiary"
                        iconOnly
                        ssrIcon={hasAddedToBag ? shoppingBagActive : shoppingBag}
                    />
                    <Button onClick={onDoneClick} type="tertiary" iconOnly ssrIcon={cross} />
                </div>
            </div>
            <div className="modalSubHeader">
                <Button onClick={closeModal} type="secondary" ssrIcon={arrowLeft} text={t('design.edit design')} />

                {showSaveButton && (
                    <Button
                        onClick={onSaveClick}
                        disabled={isSaving || !isModified}
                        type="secondary"
                        text={saveButtonText}
                    />
                )}
            </div>
            <div className="modalBody">
                <div className="compositionImgContainer">
                    <div className="compositionName">{compositionName}</div>
                    <img className="modalCompositionImage" src="" alt="" ref={panoImageRef} />
                </div>

                <div>
                    <div className="shopHeader">
                        <div className="shopDesign">{t('designSummary.shop this design')}</div>
                        <div className="priceValue">{totalPrice}</div>
                    </div>

                    <div className="ctas">
                        <Button onClick={addRoomToBag} type="emphasised" text={t('design.add room to shopping bag')} />
                        {hasItemsToFavorite && (
                            <div className="favoriteAllCta">
                                <FavoriteButtonContainer
                                    localItemNumbers={localItemNumbers}
                                    location={TRACK_LOCATION_DESIGN_SUMMARY}
                                />
                            </div>
                        )}
                    </div>

                    {hasProductsInRoom && (
                        <div className="productsInRoom">
                            {addedToRoomItems.map((productData) => (
                                <ProductItemContainer
                                    furnitureData={productData}
                                    key={productData.uniqueId || productData.id}
                                    isInDesignSummaryView
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

DesignSummaryModal.defaultProps = {
    localItemNumbers: null,
    onCloseMagicWindow: null,
}

DesignSummaryModal.propTypes = {
    closeModal: func.isRequired,
    composition: compositionType.isRequired,
    addRoomToBag: func.isRequired,
    hasAddedToBag: bool.isRequired,
    totalPrice: string.isRequired,
    localItemNumbers: arrayOf(string),
    openExitPopup: func.isRequired,
    addedToRoomItems: productsDataType.isRequired,
    openBag: func.isRequired,
    isModified: bool.isRequired,
    openDesignLab: func.isRequired,
    ikeaNav: bool.isRequired,
    onCloseMagicWindow: func,
    isMagicWindow: bool.isRequired,
    isLoggedIn: bool.isRequired,
    isSaving: bool.isRequired,
    onSave: func.isRequired,
}

export default DesignSummaryModal
